import { Text, TextProps } from '@mantine/core'
import { useEffect, useRef, useState } from 'react'

import { DEFAULT_CURRENCY, getCurrencyInfo } from '@/configs/currencies'
import { useSsrContext } from '@/contexts/ssr'
import { useCurrencyAmounts } from '@/hooks/business/useCurrencyAmounts'
import { fiatCurrencyFormat, formatCurrencyAmount } from '@/utils/number/format'

import styles from './index.module.css'

interface AnimatedPriceProps extends TextProps {
  /**
   * usd price
   */
  price?: number
  tokenSymbol: string
  disabledPriceAnimate?: boolean
  disabledConvert?: boolean
}

function useFormattedOriginValue(price?: number) {
  const { selectedCurrency } = useSsrContext()
  const currentCurrency = getCurrencyInfo(selectedCurrency) || DEFAULT_CURRENCY
  return formatCurrencyAmount(fiatCurrencyFormat(price ?? 0), currentCurrency, {})
}

export default function AnimatedPrice({
  price,
  tokenSymbol,
  disabledPriceAnimate,
  disabledConvert,
  ...textProps
}: AnimatedPriceProps) {
  const {
    amounts: [{ formattedValue }],
  } = useCurrencyAmounts({
    amounts: [price],
    currencyFormatOptions: {},
    tokenSymbol,
  })
  const notConvertedValue = useFormattedOriginValue(price)
  const displayValue = disabledConvert ? notConvertedValue : formattedValue

  const prePriceRef = useRef(price)
  const [changeClass, setChangeClass] = useState('')
  useEffect(() => {
    const prePrice = prePriceRef.current
    prePriceRef.current = price
    if (prePrice === undefined || price === undefined || disabledPriceAnimate) return
    if (price > prePrice) {
      setChangeClass(styles.priceUp)
    } else if (price < prePrice) {
      setChangeClass(styles.priceDown)
    } else {
      setChangeClass('')
    }
  }, [price, disabledPriceAnimate])

  return (
    <Text
      {...textProps}
      className={changeClass}
      title={formattedValue}
      onAnimationEnd={() => {
        setChangeClass('')
      }}
    >
      {displayValue}
    </Text>
  )
}
